import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  X,
  Pencil,
  Combine,
  Scissors,
  RefreshCw,
  Search,
  Trash2,
  RotateCcw,
  ShieldOff,
  AlertOctagon,
  ListChecks,
  Eye,
  Plus,
  Ban,
  Download,
  Upload as UploadIcon,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import employeesCanonical from "@/api/employees-canonical";
import { formatApiError } from "@/api/client";
import HelpTooltip from "@/components/HelpTooltip";
import AliasBadge, { SOURCE_LABELS } from "./_employees-canonical/AliasBadge";
import CreateEmployeeDialog from "./_employees-canonical/CreateEmployeeDialog";
import EditEmployeeDialog from "./_employees-canonical/EditEmployeeDialog";
import ImportCanonicalsDialog from "./_employees-canonical/ImportCanonicalsDialog";
import EmployeesCleanupDialog from "./EmployeesCleanupDialog";

export default function EmployeesCanonical() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showInactive, setShowInactive] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null); // either a row or "BULK"
  const [hardDeleteText, setHardDeleteText] = useState("");
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergePrimary, setMergePrimary] = useState(null);
  const [mergeSecondary, setMergeSecondary] = useState("");
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitSource, setSplitSource] = useState(null);
  const [splitAliasKeys, setSplitAliasKeys] = useState(new Set());
  const [splitNewName, setSplitNewName] = useState("");
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Phase 10.6 — Import canonicals from a previous export.
  const [importOpen, setImportOpen] = useState(false);
  // Batch 2 (Tarea 2) — full edit modal + individual deactivate confirmation.
  const [editRow, setEditRow] = useState(null);
  const [deactivateRow, setDeactivateRow] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, bl] = await Promise.all([
        employeesCanonical.list(),
        employeesCanonical.listBlacklist(),
      ]);
      setRows(r.data || []);
      setBlacklist(bl.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, bl] = await Promise.all([
          employeesCanonical.list(),
          employeesCanonical.listBlacklist(),
        ]);
        if (cancelled) return;
        setRows(r.data || []);
        setBlacklist(bl.data || []);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let base = rows;
    if (!showInactive) base = base.filter((r) => r.active !== false);
    if (!s) return base;
    return base.filter(
      (r) =>
        r.canonical_name.toLowerCase().includes(s) ||
        r.aliases.some((a) => a.code.toLowerCase().includes(s))
    );
  }, [rows, search, showInactive]);

  const inactiveCount = useMemo(
    () => rows.filter((r) => r.active === false).length,
    [rows]
  );

  const doRebuild = async () => {
    try {
      const r = await employeesCanonical.rebuild();
      toast.success(
        `${t("employeesCanonical.rebuilt")} · +${r.data.inserted}/~${r.data.updated} · ⛔${r.data.skipped ?? 0}`
      );
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Phase 10.6 — download an export of every canonical + blacklisted name.
  // The backend returns JSON with a `Content-Disposition: attachment` header
  // but we want to bypass axios's default text handling so the browser fires
  // a real download instead of just parsing the body into memory. Blob + a
  // synthesised <a> click is the simplest cross-browser recipe.
  //
  // Phase 10.8 (P0 hotfix) — Prefer the filename the backend already
  // formats (`canonicals_export_YYYYMMDD_HHMMSS.json` via
  // `Content-Disposition`) instead of the JS `toISOString()` fallback,
  // which produced ugly names like `2026-08-19T20-17-07.json`. Also
  // adds a loading state so the admin can tell the click registered
  // and a specific toast on network / blob failures.
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await employeesCanonical.export();
      const data = response?.data;
      if (!data || typeof data !== "object") {
        throw new Error("Respuesta del servidor sin cuerpo JSON.");
      }

      // Try to reuse the server-provided filename first.
      let filename = null;
      const cd = response?.headers?.["content-disposition"];
      if (cd && typeof cd === "string") {
        // Handles both `filename="…"` and `filename=…` forms.
        const m = cd.match(/filename\*?=(?:UTF-8''|"|)([^";]+)"?/i);
        if (m && m[1]) filename = decodeURIComponent(m[1].trim());
      }
      if (!filename) {
        const ts = new Date()
          .toISOString()
          .replace(/[-:T]/g, "")
          .slice(0, 15); // -> YYYYMMDDHHMMSS
        filename = `canonicals_export_${ts.slice(0, 8)}_${ts.slice(
          8,
          14,
        )}.json`;
      }

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Give Safari a tick to actually consume the URL before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success(
        `Exportados ${data.counts?.canonicals ?? 0} canónicos y ${
          data.counts?.blocked_names ?? 0
        } bloqueados · ${filename}`,
      );
    } catch (err) {
      // Blob / anchor.click() failures throw synchronously (no HTTP
      // response), so `formatApiError` may return "Network Error" —
      // fall back to the raw message so the admin sees something
      // actionable instead of a generic "Error de red".
      const detail =
        err?.response?.data?.detail ||
        formatApiError(err) ||
        err?.message ||
        "Error desconocido al exportar.";
      toast.error(`No se pudo exportar: ${detail}`);
      // Log the exception fully in the browser console so QA / devs can
      // grab the stack even after the toast dismisses.
      // eslint-disable-next-line no-console
      console.error("Export canonicals failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const doMerge = async () => {
    if (!mergePrimary || !mergeSecondary) return;
    try {
      await employeesCanonical.merge(mergePrimary.id, mergeSecondary);
      toast.success(t("employeesCanonical.merged"));
      setMergeOpen(false);
      setMergePrimary(null);
      setMergeSecondary("");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openSplit = (r) => {
    setSplitSource(r);
    setSplitAliasKeys(new Set());
    setSplitNewName(`${r.canonical_name} (split)`);
    setSplitOpen(true);
  };
  const doSplit = async () => {
    if (!splitSource || splitAliasKeys.size === 0) return;
    try {
      const aliases_to_split = splitSource.aliases.filter((a) =>
        splitAliasKeys.has(`${a.code}|${a.source}`)
      );
      await employeesCanonical.split(
        splitSource.id,
        aliases_to_split,
        splitNewName
      );
      toast.success(t("employeesCanonical.splitDone"));
      setSplitOpen(false);
      setSplitSource(null);
      setSplitAliasKeys(new Set());
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---------- Bulk delete + reactivate ----------
  const visibleIds = filtered.map((r) => r.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  const toggleRow = (id) =>
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelectedIds((s) => {
      const next = new Set(s);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const r = await employeesCanonical.bulkDelete(ids);
      toast.success(`${t("employeesCanonical.deletedToast")} · ${r.data.modified}`);
      clearSelection();
      setDeleteOpen(false);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doReactivate = async (id) => {
    try {
      await employeesCanonical.reactivate(id);
      toast.success(t("employeesCanonical.reactivated"));
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Batch 2 (Tarea 2) — soft deactivate a single canonical (active=False).
  // History preserved; the row moves to the inactive bucket where it can be
  // reactivated with a single click. Uses the same PATCH endpoint the edit
  // modal uses under the hood so the audit trail (updated_at, updated_by)
  // stays consistent.
  const doDeactivate = async () => {
    if (!deactivateRow) return;
    try {
      await employeesCanonical.update(deactivateRow.id, { active: false });
      toast.success(t("employeesCanonical.deactivated"));
      setDeactivateRow(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---------- Phase 4 Fix C — hard delete (purge) ----------
  const openHardDelete = (target) => {
    setHardDeleteTarget(target);
    setHardDeleteText("");
    setHardDeleteOpen(true);
  };

  const doHardDelete = async () => {
    if (!hardDeleteTarget) return;
    try {
      if (hardDeleteTarget === "BULK") {
        const r = await employeesCanonical.purgeInactive();
        toast.success(
          `${t("employeesCanonical.purged")} · ${r.data.deleted} · 🚫${r.data.blacklisted}`
        );
      } else {
        const r = await employeesCanonical.hardDeleteOne(hardDeleteTarget.id);
        toast.success(`${t("employeesCanonical.purgedOne")} · 🚫${r.data.blacklisted}`);
      }
      setHardDeleteOpen(false);
      setHardDeleteTarget(null);
      setHardDeleteText("");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doUnblock = async (nameNorm) => {
    try {
      await employeesCanonical.unblockBlacklist(nameNorm);
      toast.success(t("employeesCanonical.unblocked"));
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-canonical-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" data-testid="employees-canonical-title">
            {t("employeesCanonical.title")}
            <HelpTooltip anchor="employees" label={t("help.tooltip.employees")} testId="help-tooltip-employees" className="ml-2 align-middle" />
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("employeesCanonical.subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="create-employee-btn"
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            {t("employeesCanonical.createButton")}
          </Button>
          <Button
            onClick={() => setCleanupOpen(true)}
            variant="outline"
            data-testid="cleanup-canonicals-btn"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
          >
            🧹 Limpiar lista canónica
          </Button>
          <Button
            onClick={handleExport}
            variant="outline"
            disabled={exporting}
            data-testid="export-canonicals-btn"
            title="Descargar JSON con todos los canónicos + bloqueados"
          >
            {exporting ? (
              <>
                <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                Exportando…
              </>
            ) : (
              <>
                <Download className="mr-2 h-3.5 w-3.5" />
                Exportar (JSON)
              </>
            )}
          </Button>
          <Button
            onClick={() => setImportOpen(true)}
            variant="outline"
            data-testid="import-canonicals-btn"
            title="Restaurar canónicos desde un JSON exportado"
          >
            <UploadIcon className="mr-2 h-3.5 w-3.5" />
            Importar (JSON)
          </Button>
          <Button onClick={doRebuild} variant="outline" data-testid="rebuild-index-btn">
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            {t("employeesCanonical.rebuild")}
          </Button>
        </div>
      </div>

      <CreateEmployeeDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={refresh}
      />

      <ImportCanonicalsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={refresh}
      />

      <EditEmployeeDialog
        open={!!editRow}
        onOpenChange={(v) => !v && setEditRow(null)}
        row={editRow}
        onSaved={refresh}
      />

      <AlertDialog
        open={!!deactivateRow}
        onOpenChange={(v) => !v && setDeactivateRow(null)}
      >
        <AlertDialogContent data-testid="deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-400">
              <Ban className="h-5 w-5" />
              {t("employeesCanonical.deactivateTitle", {
                name: deactivateRow?.canonical_name || "",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("employeesCanonical.deactivateDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeactivate}
              data-testid="deactivate-confirm"
              className="bg-amber-500 text-black hover:bg-amber-500/90"
            >
              <Ban className="mr-1.5 h-3.5 w-3.5" />
              {t("employeesCanonical.deactivateConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EmployeesCleanupDialog
        open={cleanupOpen}
        onOpenChange={setCleanupOpen}
        onApplied={refresh}
      />

      <Tabs defaultValue="employees" data-testid="employees-canonical-tabs">
        <TabsList>
          <TabsTrigger value="employees" data-testid="tab-employees">
            <ListChecks className="mr-1.5 h-3.5 w-3.5" />
            {t("employeesCanonical.title")}
            <Badge variant="secondary" className="ml-2 text-[10px]">
              {rows.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="blacklist" data-testid="tab-blacklist">
            <ShieldOff className="mr-1.5 h-3.5 w-3.5" />
            {t("employeesCanonical.blacklistTitle")}
            <Badge variant="secondary" className="ml-2 text-[10px]">
              {blacklist.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("common.search")}
                className="h-9 w-72 pl-8"
                data-testid="employees-canonical-search"
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {filtered.length} / {rows.length}
            </span>
            <label className="ml-2 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={showInactive}
                onCheckedChange={(v) => setShowInactive(!!v)}
                data-testid="toggle-show-inactive"
              />
              <Eye className="h-3.5 w-3.5" />
              {t("employeesCanonical.showInactive")}
            </label>
            <div className="flex-1" />
            {inactiveCount > 0 && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => openHardDelete("BULK")}
                data-testid="purge-inactive-btn"
                className="h-9"
              >
                <AlertOctagon className="mr-1.5 h-3.5 w-3.5" />
                {t("employeesCanonical.purgeInactive", { n: inactiveCount })}
              </Button>
            )}
          </div>

          {/* Sticky action bar — appears whenever ≥1 row is selected. */}
          {selectedIds.size > 0 && (
            <div
              className="sticky top-2 z-20 flex items-center justify-between rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 backdrop-blur"
              data-testid="bulk-action-bar"
            >
              <div className="flex items-center gap-3">
                <span
                  className="text-sm font-medium text-foreground"
                  data-testid="bulk-selected-count"
                >
                  {t("employeesCanonical.selectedCount", { n: selectedIds.size })}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearSelection}
                  data-testid="bulk-clear-selection"
                  className="h-8"
                >
                  <X className="mr-1 h-3.5 w-3.5" />
                  {t("employeesCanonical.clearSelection")}
                </Button>
              </div>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
                data-testid="bulk-delete-btn"
                className="h-8"
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                {t("employeesCanonical.deleteSelected")}
              </Button>
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("employeesCanonical.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table data-testid="employees-canonical-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={
                            allVisibleSelected
                              ? true
                              : someVisibleSelected
                              ? "indeterminate"
                              : false
                          }
                          onCheckedChange={toggleAllVisible}
                          aria-label={t("employeesCanonical.selectAll")}
                          data-testid="canonical-select-all"
                        />
                      </TableHead>
                      <TableHead className="text-xs">{t("common.name")}</TableHead>
                      <TableHead className="text-xs">{t("employeesCanonical.aliases")}</TableHead>
                      <TableHead className="text-right text-xs">{t("efficiency.transactions")}</TableHead>
                      <TableHead className="text-right text-xs">{t("efficiency.hours")}</TableHead>
                      <TableHead className="text-right text-xs">tx/hr</TableHead>
                      <TableHead className="text-xs">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-12 text-center text-sm text-muted-foreground">
                          {loading ? t("common.loading") : t("common.no_data")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((r) => {
                        const inactive = r.active === false;
                        const selected = selectedIds.has(r.id);
                        return (
                          <TableRow
                            key={r.id}
                            data-testid={`canonical-row-${r.id}`}
                            data-active={inactive ? "false" : "true"}
                            className={inactive ? "opacity-60" : ""}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selected}
                                onCheckedChange={() => toggleRow(r.id)}
                                aria-label="select-row"
                                data-testid={`canonical-select-${r.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {r.canonical_name}
                                {r.merged_manually && !inactive && (
                                  <Badge variant="secondary" className="ml-2 text-[10px]">
                                    manual
                                  </Badge>
                                )}
                                {inactive && (
                                  <Badge
                                    variant="outline"
                                    className="ml-2 border-destructive/40 text-[10px] text-destructive"
                                    data-testid={`canonical-inactive-${r.id}`}
                                  >
                                    {t("employeesCanonical.inactiveBadge")}
                                  </Badge>
                                )}
                                {inactive && r.deactivated_reason && (
                                  // Phase 10.7 — Surface *why* the canonical
                                  // is inactive. Keep it tiny so it doesn't
                                  // fight the name for attention, but make
                                  // the reason machine-readable via
                                  // `data-reason` for the QA agent.
                                  <Badge
                                    variant="outline"
                                    className="ml-1.5 border-amber-500/40 text-[10px] text-amber-300"
                                    title={r.deactivated_reason}
                                    data-testid={`canonical-inactive-reason-${r.id}`}
                                    data-reason={r.deactivated_reason}
                                  >
                                    {r.deactivated_reason.startsWith(
                                      "no_activity_te350_",
                                    )
                                      ? `Sin actividad TE350${
                                          r.deactivated_at
                                            ? ` · ${r.deactivated_at.slice(
                                                5,
                                                10,
                                              )}`
                                            : ""
                                        }`
                                      : r.deactivated_reason.slice(0, 30)}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {r.aliases.map((a, i) => (
                                  <AliasBadge
                                    key={`${a.code}-${a.source}-${i}`}
                                    alias={a}
                                  />
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(r.total_transactions ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(r.total_hours ?? 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {r.tx_per_hour != null ? r.tx_per_hour.toFixed(2) : "—"}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {inactive ? (
                                  <>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => doReactivate(r.id)}
                                      data-testid={`canonical-reactivate-${r.id}`}
                                      title={t("employeesCanonical.reactivate")}
                                    >
                                      <RotateCcw className="h-3.5 w-3.5 text-emerald-500" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => openHardDelete(r)}
                                      data-testid={`canonical-purge-${r.id}`}
                                      title={t("employeesCanonical.hardDelete")}
                                    >
                                      <AlertOctagon className="h-3.5 w-3.5 text-destructive" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => setEditRow(r)}
                                      data-testid={`canonical-edit-${r.id}`}
                                      title={t("common.edit")}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => {
                                        setMergePrimary(r);
                                        setMergeOpen(true);
                                      }}
                                      data-testid={`canonical-merge-btn-${r.id}`}
                                      title={t("employeesCanonical.merge")}
                                    >
                                      <Combine className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => openSplit(r)}
                                      disabled={r.aliases.length < 2}
                                      data-testid={`canonical-split-btn-${r.id}`}
                                      title={t("employeesCanonical.split")}
                                    >
                                      <Scissors className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => setDeactivateRow(r)}
                                      data-testid={`canonical-deactivate-${r.id}`}
                                      title={t("employeesCanonical.deactivate")}
                                      className="text-amber-400 hover:bg-amber-500/10"
                                    >
                                      <Ban className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blacklist" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldOff className="h-4 w-4" />
                {t("employeesCanonical.blacklistTitle")}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {t("employeesCanonical.blacklistDescription")}
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table data-testid="blacklist-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">{t("common.name")}</TableHead>
                      <TableHead className="text-xs">{t("employeesCanonical.blacklistedAt")}</TableHead>
                      <TableHead className="text-xs">{t("employeesCanonical.blacklistedBy")}</TableHead>
                      <TableHead className="text-xs">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blacklist.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-12 text-center text-sm text-muted-foreground">
                          {t("employeesCanonical.blacklistEmpty")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      blacklist.map((b) => (
                        <TableRow key={b.name_norm} data-testid={`blacklist-row-${b.name_norm}`}>
                          <TableCell className="font-medium">{b.name_display}</TableCell>
                          <TableCell className="text-xs tabular-nums text-muted-foreground">
                            {b.blacklisted_at?.slice(0, 19).replace("T", " ") || "—"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {b.blacklisted_by || "—"}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => doUnblock(b.name_norm)}
                              data-testid={`blacklist-unblock-${b.name_norm}`}
                            >
                              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                              {t("employeesCanonical.unblock")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Merge dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>{t("employeesCanonical.mergeTitle")}</DialogTitle>
            <DialogDescription>
              {t("employeesCanonical.mergeDescription", { name: mergePrimary?.canonical_name })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs">{t("employeesCanonical.mergePickSecondary")}</Label>
            <Select value={mergeSecondary} onValueChange={setMergeSecondary}>
              <SelectTrigger data-testid="merge-secondary-select">
                <SelectValue placeholder={t("employeesCanonical.mergePickSecondary")} />
              </SelectTrigger>
              <SelectContent>
                {rows
                  .filter((r) => r.id !== mergePrimary?.id && r.active !== false)
                  .map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`merge-option-${r.id}`}>
                      {r.canonical_name} ({r.aliases.map((a) => a.code).join(", ") || "no aliases"})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} data-testid="merge-cancel">
              {t("common.cancel")}
            </Button>
            <Button onClick={doMerge} disabled={!mergeSecondary} data-testid="merge-confirm">
              {t("employeesCanonical.merge")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split dialog */}
      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent data-testid="split-dialog">
          <DialogHeader>
            <DialogTitle>{t("employeesCanonical.splitTitle")}</DialogTitle>
            <DialogDescription>
              {t("employeesCanonical.splitDescription", { name: splitSource?.canonical_name })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("employeesCanonical.splitNewName")}</Label>
              <Input
                value={splitNewName}
                onChange={(e) => setSplitNewName(e.target.value)}
                data-testid="split-new-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("employeesCanonical.splitPickAliases")}</Label>
              <div className="flex flex-wrap gap-2 rounded-md border p-3">
                {splitSource?.aliases.map((a) => {
                  const key = `${a.code}|${a.source}`;
                  const picked = splitAliasKeys.has(key);
                  return (
                    <button
                      key={key}
                      onClick={() =>
                        setSplitAliasKeys((s) => {
                          const next = new Set(s);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        })
                      }
                      data-testid={`split-alias-toggle-${a.code}-${a.source}`}
                      className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] transition-colors ${
                        picked
                          ? "border-primary bg-primary/20 text-primary"
                          : "border-border bg-card text-muted-foreground hover:bg-accent/40"
                      }`}
                    >
                      {a.code} · {SOURCE_LABELS[a.source] || a.source}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitOpen(false)} data-testid="split-cancel">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={doSplit}
              disabled={splitAliasKeys.size === 0 || !splitNewName.trim()}
              data-testid="split-confirm"
            >
              {t("employeesCanonical.split")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Soft bulk-delete confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("employeesCanonical.deleteTitle", { n: selectedIds.size })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("employeesCanonical.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="bulk-delete-confirm"
            >
              {t("employeesCanonical.deleteSelected")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard delete (purge) — requires typing "BORRAR" to confirm */}
      <Dialog
        open={hardDeleteOpen}
        onOpenChange={(v) => {
          setHardDeleteOpen(v);
          if (!v) {
            setHardDeleteTarget(null);
            setHardDeleteText("");
          }
        }}
      >
        <DialogContent data-testid="hard-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertOctagon className="h-5 w-5" />
              {hardDeleteTarget === "BULK"
                ? t("employeesCanonical.purgeTitleBulk", { n: inactiveCount })
                : t("employeesCanonical.purgeTitleOne", {
                    name: hardDeleteTarget?.canonical_name || "",
                  })}
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <span>{t("employeesCanonical.purgeDescription")}</span>
              <span className="block rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-foreground">
                {t("employeesCanonical.purgeWarning")}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs">
              {t("employeesCanonical.purgeTypeBORRAR")}
            </Label>
            <Input
              value={hardDeleteText}
              onChange={(e) => setHardDeleteText(e.target.value)}
              placeholder="BORRAR"
              className="font-mono"
              data-testid="hard-delete-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHardDeleteOpen(false)}
              data-testid="hard-delete-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={hardDeleteText.trim() !== "BORRAR"}
              onClick={doHardDelete}
              data-testid="hard-delete-confirm"
            >
              <AlertOctagon className="mr-1.5 h-3.5 w-3.5" />
              {t("employeesCanonical.purgeConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
